import type { AdminApi } from "@/api";
import { Card, Disclosure, EmptyState, Section } from "@/components/ui";
import type { AdminPromptState, SubAgentProfile } from "@/types";
import { PromptCallSitesPanel } from "./PromptCallSitesPanel";
import { PromptDeploymentPanel } from "./PromptDeploymentPanel";
import { PROMPT_TARGETS } from "./promptTargets";
import { PromptVersionsPanel } from "./PromptVersionsPanel";
import { SubAgentVersionsPanel } from "./SubAgentVersionsPanel";

const SUB_AGENT_ROLES: SubAgentProfile["role"][] = ["orchestrator", "web", "library", "browser"];

// No title, description or Reload here: the shell's page header owns all three
// now (AdminShell + pageMeta). Keeping a copy meant every screen showed its
// name twice.
export function PromptLabPage({
  state,
  api,
  onFlash,
  onError,
  prodAccess = false,
}: {
  state: AdminPromptState | null;
  api: AdminApi;
  /** The login may deploy to prod (`/admin/sams/admins/me`); the server checks it again. */
  prodAccess?: boolean;
  onFlash: (message: string) => void;
  onError: (error: unknown) => void;
}) {
  return (
    <div className="grid gap-4.5">
      <PromptDeploymentPanel api={api} prodAccess={prodAccess} />
      {!state ? (
        <EmptyState title="프롬프트를 아직 불러오지 않았습니다" body="위쪽의 [Reload] 버튼을 누르면 dev DB의 사용 중인 프롬프트를 읽습니다." />
      ) : (
        <>
          <Section title="미리보기용 가상 사용자">
            <div className="grid grid-cols-3 gap-3">
              {state.fake_users.map((user) => (
                <Card key={user.email} className="grid gap-1.5 p-3.5">
                  <strong>{user.display_name}</strong>
                  <small className="text-xs text-muted-foreground">{user.email}</small>
                  <p className="mb-4">{user.description}</p>
                  <small className="text-xs text-muted-foreground">사용자 지침: {user.user_instruction}</small>
                  {user.session_instruction && <small className="text-xs text-muted-foreground">세션 지침: {user.session_instruction}</small>}
                </Card>
              ))}
            </div>
          </Section>
          <Section title="프롬프트">
            <Card className="mb-3 grid gap-1.5 p-3.5">
              <strong>버전 저장과 사용 버전 관리</strong>
              <p className="mb-0 text-sm text-muted-foreground">
                저장하면 새 버전이 생깁니다. 서비스에 쓰는 프롬프트는 [적용]을 눌러야 사용 버전이 바뀝니다.
                평가 실행은 직접 고른 버전으로 고정되므로, 사용 버전을 바꿔도 이미 만든 작업이나 자동 설정은 바뀌지 않습니다.
                저장할 때마다 어느 버전에서 고쳤는지 기록되고, AI가 바뀐 점을 요약합니다.
                템플릿은 Jinja입니다. 코드는 값만 넘기고, 조건에 따라 달라지는 문구는 템플릿에 둡니다.
                Base 프롬프트는 공통 대화·정체성·권한·출력 규칙을 맡고, 오케스트레이터는 위임을 맡습니다.
                웹·라이브러리·브라우저 프로필은 각자의 전문 규칙을 맡습니다.
              </p>
            </Card>
            {PROMPT_TARGETS.map((spec) => (
              <PromptVersionsPanel
                key={spec.target}
                api={api}
                scope={{ target: spec.target }}
                spec={spec}
                onFlash={onFlash}
                onError={onError}
                footer={<PromptCallSitesPanel api={api} target={spec.target} />}
              />
            ))}
          </Section>
          <Section title="하위 에이전트 프로필">
            {SUB_AGENT_ROLES.map((role) => (
              <SubAgentVersionsPanel key={role} api={api} role={role} onFlash={onFlash} onError={onError} />
            ))}
          </Section>
          <Section title="서비스에서 조립되는 프롬프트 미리보기">
            {state.previews.map((preview) => (
              <Disclosure key={preview.key} title={preview.title} subtitle={preview.description} defaultOpen={false}>
                <pre className="mt-4 block max-w-full overflow-x-hidden rounded-lg border border-border bg-surface-input p-3 font-mono whitespace-pre-wrap text-code-foreground wrap-anywhere max-h-none">{preview.body}</pre>
              </Disclosure>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}
